import type { IQuizRepository } from '@/core/domain/quiz/IQuizRepository.ts'
import { apiService } from '@/core/infrastructure/services/apiServiceFactory'
import { Quiz } from '@/core/domain/quiz/Quiz.ts'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'
import type { CreateQuizVersionPayload } from '@/core/useCases/quiz/CreateQuizVersionUseCase.ts'
import { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'

export class QuizRepository implements IQuizRepository {
  /**
   * Récupère un quiz spécifique par son ID
   * @param id L'ID du quiz
   */
  async fetchQuiz(id: number): Promise<Quiz> {
    try {
      const response = await apiService.get(`/quiz/${id}`)

      return Quiz.create(response.data)
    } catch (error) {
      console.error('Erreur lors de la récupération du quiz:', error)
      return Promise.reject(`Erreur lors de la récupération du quiz: ${error}`)
    }
  }

  /**
   * Enregistre les modifications d'un quiz existant
   * @param quizId Le quiz à sauvegarder
   * @param questions Les questions du quiz
   */
  async updateQuiz(quizId: number, questions: QuizQuestion[]): Promise<Quiz> {
    try {
      const response = await apiService.put(`/quiz/${quizId}`, questions)

      return Quiz.create(response.data)
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du quiz:', error)
      return Promise.reject(`Erreur lors de la sauvegarde du quiz: ${error}`)
    }
  }

  /**
   * Enregistre les modifications d'une version spécifique d'un quiz
   * @param quizId L'ID du quiz
   * @param version La version complète à mettre à jour
   */
  async updateQuizVersion(quizId: number, version: QuizVersion): Promise<QuizVersion> {
    try {
      const payload = version.toUpdatePayload()
      const response = await apiService.put(`/quiz/${quizId}/versions/${version.id}`, payload)

      return QuizVersion.create(response.data)
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la version du quiz:', error)
      return Promise.reject(`Erreur lors de la sauvegarde de la version du quiz: ${error}`)
    }
  }

  /**
   * Marque un quiz comme prêt
   * @param quizId L'ID du quiz
   */
  async markQuizAsReady(quizId: number): Promise<Quiz> {
    try {
      const response = await apiService.put(`/quiz/${quizId}/ready`, {})

      return Quiz.create(response.data)
    } catch (error) {
      console.error('Erreur lors de la publication du quiz:', error)
      return Promise.reject(`Erreur lors de la publication du quiz: ${error}`)
    }
  }

  /**
   * Crée une nouvelle version d'un quiz
   * @param quizId L'ID du quiz
   * @param payload Les données de la nouvelle version
   */
  async createQuizVersion(quizId: number, payload: CreateQuizVersionPayload): Promise<QuizVersion> {
    try {
      const response = await apiService.post(
        `/quiz/${quizId}/versions${payload.fromVersionId ? `?fromVersionId=${payload.fromVersionId}` : ''}`
      )

      return QuizVersion.create(response.data)
    } catch (error) {
      console.error('Erreur lors de la création de la version:', error)
      return Promise.reject(`Erreur lors de la création de la version: ${error}`)
    }
  }

  /**
   * Active une version spécifique d'un quiz
   * @param quizId L'ID du quiz
   * @param versionId L'ID de la version à activer
   */
  async activateQuizVersion(quizId: number, versionId: number): Promise<Quiz> {
    try {
      const response = await apiService.put(`/quiz/${quizId}/versions/${versionId}/active`, {})

      return Quiz.create(response.data)
    } catch (error) {
      console.error("Erreur lors de l'activation de la version:", error)
      return Promise.reject(`Erreur lors de l'activation de la version: ${error}`)
    }
  }

  /**
   * Archive une version spécifique d'un quiz
   * @param quizId L'ID du quiz
   * @param versionId L'ID de la version à archiver
   */
  async archiveQuizVersion(quizId: number, versionId: number): Promise<Quiz> {
    try {
      const response = await apiService.put(`/quiz/${quizId}/versions/${versionId}/archive`, {})

      return Quiz.create(response.data)
    } catch (error) {
      console.error("Erreur lors de l'archivage de la version:", error)
      return Promise.reject(`Erreur lors de l'archivage de la version: ${error}`)
    }
  }

  /**
   * Supprime une version brouillon d'un quiz
   * @param quizId L'ID du quiz
   * @param versionId L'ID de la version à supprimer
   */
  async deleteQuizVersion(quizId: number, versionId: number): Promise<void> {
    try {
      await apiService.delete(`/quiz/${quizId}/versions/${versionId}`)
    } catch (error) {
      console.error('Erreur lors de la suppression de la version:', error)
      return Promise.reject(`Erreur lors de la suppression de la version: ${error}`)
    }
  }
}
